import { useCallback, useEffect, useMemo, useState } from "react";

export interface TableSort {
  columnId: string;
  direction: "asc" | "desc";
}

export interface TableViewConfig {
  visibleColumnIds: string[];
  statusFilter: string[];
  textFilter: string;
  /** Per-field multi-select filters, keyed by field id (e.g. companyName). */
  fieldFilters: Record<string, string[]>;
  /** Per-column width overrides (px), keyed by column id. */
  columnWidths: Record<string, number>;
  /** Column ids in display order. Columns missing here keep their registry order. */
  columnOrder: string[];
  /** Row ordering; null keeps the order the API returns. */
  sort: TableSort | null;
}

const isStringArray = (v: unknown): v is string[] =>
  Array.isArray(v) && v.every((x) => typeof x === "string");

const isPlainObject = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);

/**
 * Turns anything stored (localStorage, a saved view from the API) into a
 * complete config, falling back to `defaults` field by field.
 */
export function normalizeTableViewConfig(
  raw: unknown,
  defaults: TableViewConfig
): TableViewConfig {
  const saved = isPlainObject(raw) ? raw : {};
  const sort = saved.sort;
  return {
    visibleColumnIds: isStringArray(saved.visibleColumnIds)
      ? saved.visibleColumnIds
      : defaults.visibleColumnIds,
    statusFilter: isStringArray(saved.statusFilter)
      ? saved.statusFilter
      : defaults.statusFilter,
    textFilter:
      typeof saved.textFilter === "string"
        ? saved.textFilter
        : defaults.textFilter,
    fieldFilters: isPlainObject(saved.fieldFilters)
      ? (saved.fieldFilters as Record<string, string[]>)
      : defaults.fieldFilters,
    columnWidths: isPlainObject(saved.columnWidths)
      ? (saved.columnWidths as Record<string, number>)
      : defaults.columnWidths,
    columnOrder: isStringArray(saved.columnOrder)
      ? saved.columnOrder
      : defaults.columnOrder,
    sort:
      isPlainObject(sort) &&
      typeof sort.columnId === "string" &&
      (sort.direction === "asc" || sort.direction === "desc")
        ? { columnId: sort.columnId, direction: sort.direction }
        : defaults.sort,
  };
}

/**
 * Persists table view configuration (visible columns, order, filters, sort)
 * in localStorage — the working copy of the view. Saved named views live in
 * the backend and are loaded into it with `applyConfig`.
 */
export function useTableViewConfig(
  storageKey: string,
  defaults: TableViewConfig
): {
  config: TableViewConfig;
  setVisibleColumnIds: (ids: string[]) => void;
  setStatusFilter: (statuses: string[]) => void;
  setTextFilter: (text: string) => void;
  setFieldFilter: (field: string, values: string[]) => void;
  setColumnWidth: (id: string, px: number) => void;
  setColumnOrder: (ids: string[]) => void;
  setSort: (sort: TableSort | null) => void;
  applyConfig: (config: TableViewConfig) => void;
} {
  // Load and migrate saved state once on mount
  const initial = useMemo((): TableViewConfig => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return defaults;
      return normalizeTableViewConfig(JSON.parse(raw), defaults);
    } catch {
      return defaults;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentional empty deps — one-time migration at mount

  const [config, setConfig] = useState<TableViewConfig>(initial);

  // Persist whenever config changes
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(config));
    } catch {
      // ignore quota errors
    }
  }, [storageKey, config]);

  const setVisibleColumnIds = useCallback(
    (ids: string[]) =>
      setConfig((prev) => ({ ...prev, visibleColumnIds: ids })),
    []
  );
  const setStatusFilter = useCallback(
    (statuses: string[]) =>
      setConfig((prev) => ({ ...prev, statusFilter: statuses })),
    []
  );
  const setTextFilter = useCallback(
    (text: string) => setConfig((prev) => ({ ...prev, textFilter: text })),
    []
  );
  const setFieldFilter = useCallback(
    (field: string, values: string[]) =>
      setConfig((prev) => {
        const next = { ...prev.fieldFilters };
        if (values.length === 0) delete next[field];
        else next[field] = values;
        return { ...prev, fieldFilters: next };
      }),
    []
  );
  const setColumnWidth = useCallback(
    (id: string, px: number) =>
      setConfig((prev) => ({
        ...prev,
        columnWidths: { ...prev.columnWidths, [id]: px },
      })),
    []
  );
  const setColumnOrder = useCallback(
    (ids: string[]) => setConfig((prev) => ({ ...prev, columnOrder: ids })),
    []
  );
  const setSort = useCallback(
    (sort: TableSort | null) => setConfig((prev) => ({ ...prev, sort })),
    []
  );
  const applyConfig = useCallback(
    (next: TableViewConfig) => setConfig(next),
    []
  );

  return {
    config,
    setVisibleColumnIds,
    setStatusFilter,
    setTextFilter,
    setFieldFilter,
    setColumnWidth,
    setColumnOrder,
    setSort,
    applyConfig,
  };
}
